import React, { useEffect, useRef, useState } from 'react';
import dayjs from 'dayjs';
import styled from 'styled-components';

import { SwimlaneViewButton } from '@/features/canvas/components/SwimlaneViewButton';
import { STAGE_OFFSET, TAB_HEIGHT } from '@/features/canvas/constants';
import { useEditorContext } from '@/features/canvas/contexts/editor-context';

import {
  GanttProgressColumn,
  GanttProgressHeader,
  PROGRESS_COLUMN_WIDTH,
} from './components/GanttProgressColumn';
import { GanttSidebar } from './components/GanttSidebar';
import { GanttTimeline } from './components/GanttTimeline';
import { GanttTimelineHeader } from './components/GanttTimeline/GanttTimelineHeader';
import { SubHeaderBackgroundBands } from './components/GanttTimeline/SubHeaderBackgroundBands';
import {
  GanttTitleColumn,
  GanttTitleHeader,
  TITLE_COLUMN_WIDTH,
} from './components/GanttTitleColumn';
import { GanttZoomControls } from './components/GanttZoomControls';
import { DependencyModeState } from './hooks/useDependencyMode';
import { useSectionHeights } from './hooks/useSectionHeights';
import { useTimelineBounds } from './hooks/useTimelineBounds';
import { useZoomLevel } from './hooks/useZoomLevel';

interface GanttViewProps {
  dependencyState: DependencyModeState;
}

const GanttView: React.FC<GanttViewProps> = ({ dependencyState }) => {
  const mainRef = useRef<HTMLDivElement>(null);
  const headerRef = useRef<HTMLDivElement>(null);
  const sidebarRef = useRef<HTMLDivElement>(null);
  const titleRef = useRef<HTMLDivElement>(null);
  const progressRef = useRef<HTMLDivElement>(null);
  const subHeaderRef = useRef<HTMLDivElement>(null);

  const { timelineBounds, enhancedFilteredNodes, initiativesWithFields } = useTimelineBounds();
  const { isPreview, isPublicView } = useEditorContext();
  const { updateSectionHeight, getSectionHeight } = useSectionHeights();
  const { currentZoom, zoomIn, zoomOut, canZoomIn, canZoomOut } = useZoomLevel();

  // State for expanded rows - stores initiative IDs that are expanded
  const [expandedRows, setExpandedRows] = useState<Set<number>>(new Set());

  useEffect(() => {
    const mainElement = mainRef.current;
    const headerElement = headerRef.current;
    const sidebarElement = sidebarRef.current;
    const titleElement = titleRef.current;
    const progressElement = progressRef.current;
    const subHeaderElement = subHeaderRef.current;

    if (
      !mainElement ||
      !headerElement ||
      !sidebarElement ||
      !titleElement ||
      !progressElement ||
      !subHeaderElement
    )
      return;

    const handleMainScroll = () => {
      // Sync horizontal scroll with header and subheader
      headerElement.scrollLeft = mainElement.scrollLeft;
      subHeaderElement.scrollLeft = mainElement.scrollLeft;
      // Sync vertical scroll with sidebar, title column, and progress column
      sidebarElement.scrollTop = mainElement.scrollTop;
      titleElement.scrollTop = mainElement.scrollTop;
      progressElement.scrollTop = mainElement.scrollTop;
    };

    // Forward wheel events from other columns to main container with adjusted speed
    const handleWheelEvent = (e: WheelEvent) => {
      e.preventDefault();
      // Reduce scroll speed to match native feel (typically 1/3 to 1/2 of raw delta)
      const scrollMultiplier = 0.4;
      mainElement.scrollBy({
        top: e.deltaY * scrollMultiplier,
        left: e.deltaX * scrollMultiplier,
        behavior: 'auto',
      });
    };

    mainElement.addEventListener('scroll', handleMainScroll);
    sidebarElement.addEventListener('wheel', handleWheelEvent, { passive: false });
    titleElement.addEventListener('wheel', handleWheelEvent, { passive: false });
    progressElement.addEventListener('wheel', handleWheelEvent, { passive: false });

    return () => {
      mainElement.removeEventListener('scroll', handleMainScroll);
      sidebarElement.removeEventListener('wheel', handleWheelEvent);
      titleElement.removeEventListener('wheel', handleWheelEvent);
      progressElement.removeEventListener('wheel', handleWheelEvent);
    };
  }, []);

  // Auto-scroll to center today's line when Gantt view loads
  useEffect(() => {
    const mainElement = mainRef.current;
    if (!mainElement || !timelineBounds.startDate || !timelineBounds.endDate) return;

    const today = dayjs();

    // Only auto-scroll if today is within the timeline range
    if (today.isBefore(timelineBounds.startDate) || today.isAfter(timelineBounds.endDate)) {
      return;
    }

    const totalDays = timelineBounds.endDate.diff(timelineBounds.startDate, 'days');
    const daysSinceStart = today.diff(timelineBounds.startDate, 'days');
    const todayPercent = (daysSinceStart / totalDays) * 100;

    // Calculate scroll position to center today's line
    const scrollableWidth = mainElement.scrollWidth;
    const viewportWidth = mainElement.clientWidth;
    const todayPosition = (todayPercent / 100) * scrollableWidth;
    const centerOffset = viewportWidth / 2;
    const scrollLeft = Math.max(0, todayPosition - centerOffset);

    // Scroll to position with smooth animation
    mainElement.scrollTo({
      left: scrollLeft,
      behavior: 'smooth',
    });
  }, [timelineBounds.startDate, timelineBounds.endDate, currentZoom.timelineWidth]);

  return (
    <GanttContainer>
      <CornerBlock>{(isPreview || isPublicView) && <SwimlaneViewButton />}</CornerBlock>

      <EmptyTitleHeaderSpace>
        <GanttZoomControls
          currentZoom={currentZoom}
          onZoomIn={zoomIn}
          onZoomOut={zoomOut}
          canZoomIn={canZoomIn}
          canZoomOut={canZoomOut}
        />
      </EmptyTitleHeaderSpace>

      <EmptyProgressHeaderSpace />

      <HeaderContainer ref={headerRef}>
        <ScrollableContent zoomMultiplier={currentZoom.timelineWidth}>
          <GanttTimelineHeader
            startDate={timelineBounds.startDate}
            endDate={timelineBounds.endDate}
          />
        </ScrollableContent>
      </HeaderContainer>

      <SectionHeaderSpace />

      <TitleHeaderContainer>
        <GanttTitleHeader />
      </TitleHeaderContainer>

      <ProgressHeaderContainer>
        <GanttProgressHeader />
      </ProgressHeaderContainer>

      <SubHeaderSpace ref={subHeaderRef}>
        <SubHeaderBackgroundBands
          startDate={timelineBounds.startDate}
          endDate={timelineBounds.endDate}
          zoomMultiplier={currentZoom.timelineWidth}
        />
      </SubHeaderSpace>

      <SidebarContainer ref={sidebarRef}>
        <GanttSidebar
          onSectionHeightChange={updateSectionHeight}
          enhancedFilteredNodes={enhancedFilteredNodes}
          expandedRows={expandedRows}
          initiativesWithFields={initiativesWithFields}
        />
      </SidebarContainer>

      <TitleContainer ref={titleRef}>
        <GanttTitleColumn
          enhancedFilteredNodes={enhancedFilteredNodes}
          getSectionHeight={getSectionHeight}
          isDependencyMode={dependencyState.isDependencyMode}
          expandedRows={expandedRows}
          setExpandedRows={setExpandedRows}
        />
      </TitleContainer>

      <ProgressContainer ref={progressRef}>
        <GanttProgressColumn
          enhancedFilteredNodes={enhancedFilteredNodes}
          initiativesWithFields={initiativesWithFields}
          expandedRows={expandedRows}
          getSectionHeight={getSectionHeight}
          isDependencyMode={dependencyState.isDependencyMode}
        />
      </ProgressContainer>

      <MainContainer ref={mainRef}>
        <ScrollableContent zoomMultiplier={currentZoom.timelineWidth}>
          <GanttTimeline
            startDate={timelineBounds.startDate}
            endDate={timelineBounds.endDate}
            enhancedFilteredNodes={enhancedFilteredNodes}
            initiativesWithFields={initiativesWithFields}
            expandedRows={expandedRows}
            dependencyState={dependencyState}
            getSectionHeight={getSectionHeight}
            zoomMultiplier={currentZoom.timelineWidth}
          />
        </ScrollableContent>
      </MainContainer>
    </GanttContainer>
  );
};

const GanttContainer = styled.div`
  display: grid;
  grid-template-columns: ${TAB_HEIGHT}px ${TITLE_COLUMN_WIDTH}px ${PROGRESS_COLUMN_WIDTH}px 1fr;
  grid-template-rows: 80px 30px 1fr;
  height: 100%;
  width: 100%;
  padding: ${STAGE_OFFSET}px;
  gap: 0;
  overflow: hidden; /* Contain the grid within viewport */
  box-sizing: border-box; /* Ensure padding doesn't cause overflow */
`;

const CornerBlock = styled.div`
  grid-row: 1;
  grid-column: 1;
  background: ${({ theme }) => theme.palette.brand.backgroundMain};
  display: flex;
  align-items: flex-start;
  justify-content: flex-start;
  padding: 5px;
  padding-top: 0px;
  padding-left: 0px;
`;

const EmptyTitleHeaderSpace = styled.div`
  grid-row: 1;
  grid-column: 2;
  background: ${({ theme }) => theme.palette.brand.backgroundMain};
  display: flex;
  align-items: flex-start;
  justify-content: flex-start;
  padding: 5px;
  padding-top: 0px;
  padding-left: 0px;
`;

const EmptyProgressHeaderSpace = styled.div`
  grid-row: 1;
  grid-column: 3;
  background: ${({ theme }) => theme.palette.brand.backgroundMain};
  border-right: 1px solid #afafaf;
  border-bottom: 1px solid #afafaf;
`;

const HeaderContainer = styled.div`
  grid-row: 1;
  grid-column: 4;
  overflow-x: hidden;
  overflow-y: hidden;
  position: relative;
  z-index: 10; /* Ensure content appears above CornerBlock */
  padding-right: 17px; /* Add space for vertical scrollbar compensation */

  /* Hide scrollbars */
  &::-webkit-scrollbar {
    display: none;
  }
  -ms-overflow-style: none;
  scrollbar-width: none;
`;

const SectionHeaderSpace = styled.div`
  grid-row: 2;
  grid-column: 1;
  background: ${({ theme }) => theme.palette.brand.backgroundMain};
`;

const TitleHeaderContainer = styled.div`
  grid-row: 2;
  grid-column: 2;
  background: ${({ theme }) => theme.palette.brand.backgroundMain};
  border-right: 1px solid #afafaf;
  display: flex;
  align-items: center;
  justify-content: flex-start;
`;

const ProgressHeaderContainer = styled.div`
  grid-row: 2;
  grid-column: 3;
  background: ${({ theme }) => theme.palette.brand.white};
  border-right: 1px solid #afafaf;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const SubHeaderSpace = styled.div`
  position: relative;
  grid-row: 2;
  grid-column: 4;
  background: ${({ theme }) => theme.palette.brand.backgroundMain};
  overflow: hidden;
  padding-right: 17px; /* Add space for vertical scrollbar compensation */
`;

const SidebarContainer = styled.div`
  grid-row: 3;
  grid-column: 1;
  overflow-x: hidden;
  overflow-y: hidden;
  background: ${({ theme }) => theme.palette.brand.white};
  padding-bottom: 17px; /* Add space for scrollbar compensation */

  /* Hide scrollbars */
  &::-webkit-scrollbar {
    display: none;
  }
  -ms-overflow-style: none;
  scrollbar-width: none;
`;

const TitleContainer = styled.div`
  grid-row: 3;
  grid-column: 2;
  overflow-x: hidden;
  overflow-y: hidden;
  background: ${({ theme }) => theme.palette.brand.white};
  padding-bottom: 17px; /* Add space for scrollbar compensation */

  /* Hide scrollbars */
  &::-webkit-scrollbar {
    display: none;
  }
  -ms-overflow-style: none;
  scrollbar-width: none;
`;

const ProgressContainer = styled.div`
  grid-row: 3;
  grid-column: 3;
  overflow-x: hidden;
  overflow-y: hidden;
  background: ${({ theme }) => theme.palette.brand.white};
  padding-bottom: 17px; /* Add space for scrollbar compensation */

  /* Hide scrollbars */
  &::-webkit-scrollbar {
    display: none;
  }
  -ms-overflow-style: none;
  scrollbar-width: none;
`;

const MainContainer = styled.div`
  grid-row: 3;
  grid-column: 4;
  overflow: auto;
  position: relative;
  background: ${({ theme }) => theme.palette.brand.white}; /* make the whole pane white */
`;

const ScrollableContent = styled.div<{ zoomMultiplier: number }>`
  min-width: ${({ zoomMultiplier }) => 2000 * zoomMultiplier}px;
  min-height: 100%; /* stretch to full height so the white background always shows */
  position: relative;
`;

export { GanttView };
